import {
  reportCanvasComponents,
  workbookExportFileNameSchema,
  type WorkbookDefinition,
  type WorkbookFileOutput,
  type WorkbookOutput,
  type WorkbookOutputKind,
  type WorkbookSinkOutput,
} from '@erp/shared-types';
import { outputSheetIds, workbookExecutionOrder } from '@erp/workbooks';
import type { WorkbookRunResults } from './workbookRunResults';
import { unusedName } from './workbookNames';

/** What an Output shows of the Results it reads, derived from its Worksheets. */
export type WorkbookOutputState =
  /** It reads no Worksheet yet. */
  | 'empty'
  /** A Worksheet it reads has no Result in the listed Runs. */
  | 'none'
  /** Every Worksheet it reads has a Current Result. */
  | 'current'
  /** Every Worksheet it reads has a Result, and at least one is Stale. */
  | 'stale';

/**
 * Whether an Output is Current: it computes nothing, so it is Current when
 * every Worksheet it reads is, and Stale when any of them is.
 */
export function workbookOutputState(
  definition: Pick<WorkbookDefinition, 'sheets'>,
  output: WorkbookOutput,
  results: Pick<WorkbookRunResults, 'sheet'>,
): WorkbookOutputState {
  const sheets = outputSheetIds(definition, output).map((id) =>
    results.sheet(id),
  );
  if (!sheets.length) return 'empty';
  if (sheets.some((sheet) => !sheet.result)) return 'none';
  return sheets.every((sheet) => sheet.current) ? 'current' : 'stale';
}

/** The Workbook's name as an xlsx filename, else `export.xlsx`. */
function exportFileName(name: string): string {
  const parsed = workbookExportFileNameSchema.safeParse(
    `${name.replace(/[\\/:*?"<>|]/g, ' ').trim()}.xlsx`,
  );
  return parsed.success ? parsed.data : 'export.xlsx';
}

/** The last Worksheet in Calculation Order: usually the result a Workbook builds up to. */
function lastWorksheetId(definition: WorkbookDefinition): string | undefined {
  return workbookExecutionOrder(definition)
    .filter((item) => item.kind === 'sheet' && !item.blocked)
    .at(-1)?.id;
}

/**
 * A new Data sink named `name` reading `sheetId`: no target and no mappings
 * yet, and written on demand until it is told to write after each Run.
 */
export function addSinkOutput(
  name: string,
  sheetId: string | undefined,
  id: string = crypto.randomUUID(),
): WorkbookSinkOutput {
  return { id, name, kind: 'sink', sheetId, mappings: [], deliverOnRun: false };
}

/**
 * A new Output of `kind` named `name` (numbered when taken). A File starts with
 * the last Worksheet in Calculation Order as its one tab — usually the result
 * the Workbook builds up to — and is named after the Workbook.
 */
export function newWorkbookOutput(
  definition: WorkbookDefinition,
  kind: WorkbookOutputKind,
  name: string,
  id: string = crypto.randomUUID(),
): WorkbookOutput {
  const unique = unusedName(
    definition.outputs.map((output) => output.name),
    name,
  );
  if (kind === 'report')
    return { id, name: unique, kind, layout: { items: [] } };
  if (kind === 'app') return { id, name: unique, kind };
  const last = lastWorksheetId(definition);
  if (kind === 'sink') return addSinkOutput(unique, last, id);
  return {
    id,
    name: unique,
    kind,
    fileName: exportFileName(definition.name),
    tabs: last ? [{ sheetId: last }] : [],
  };
}

type FileTab = WorkbookFileOutput['tabs'][number];

/** The tabs with the one holding `sheetId` swapped with its neighbour; unchanged past either end. */
export function movedTab(
  tabs: readonly FileTab[],
  sheetId: string,
  direction: 'up' | 'down',
): FileTab[] {
  const index = tabs.findIndex((tab) => tab.sheetId === sheetId);
  const other = index + (direction === 'up' ? -1 : 1);
  const [moving, neighbour] = [tabs[index], tabs[other]];
  if (index < 0 || !moving || !neighbour) return [...tabs];
  const next = [...tabs];
  next[index] = neighbour;
  next[other] = moving;
  return next;
}

/** A tab named `name` as typed: blank, or its Worksheet's name, names it after its Worksheet. */
export function namedTab(
  tab: FileTab,
  name: string,
  sheetName: string | undefined,
): FileTab {
  const tabName = name.trim().slice(0, 31);
  const { tabName: _previous, ...rest } = tab;
  return tabName && tabName !== sheetName ? { ...rest, tabName } : rest;
}

/** The File with a tab for `sheetId` appended; unchanged when a tab already holds it. */
export function addedTab(
  file: WorkbookFileOutput,
  sheetId: string,
): WorkbookFileOutput {
  return file.tabs.some((tab) => tab.sheetId === sheetId)
    ? file
    : { ...file, tabs: [...file.tabs, { sheetId }] };
}

/** What an Output's Process cell says it produces, from which Worksheets. */
export type WorkbookOutputSummary =
  | {
      kind: 'file';
      fileName: string;
      /** Each sheet's name as the file shows it, in order. */
      sheetNames: string[];
      /** A File of one sheet also downloads as CSV. */
      csv: boolean;
    }
  | {
      kind: 'report';
      charts: number;
      tables: number;
      /** The Worksheets its components chart, in the order they first appear. */
      sheetNames: string[];
      title?: string;
      description?: string;
    }
  | {
      kind: 'app';
      /** Absent until an App is chosen. */
      appId?: string;
    }
  | {
      kind: 'sink';
      /** Absent until a Worksheet is chosen. */
      sheetName?: string;
      mappingCount: number;
      deliverOnRun: boolean;
    };

export function workbookOutputSummary(
  definition: WorkbookDefinition,
  output: WorkbookOutput,
): WorkbookOutputSummary {
  const sheetName = (id: string) =>
    definition.sheets.find((sheet) => sheet.id === id)?.name;
  if (output.kind === 'file') {
    const sheetNames = output.tabs.flatMap((tab) => {
      const name = tab.tabName ?? sheetName(tab.sheetId);
      return name ? [name] : [];
    });
    return {
      kind: 'file',
      fileName: output.fileName,
      sheetNames,
      csv: sheetNames.length === 1,
    };
  }
  if (output.kind === 'sink')
    return {
      kind: 'sink',
      sheetName: output.sheetId ? sheetName(output.sheetId) : undefined,
      mappingCount: output.mappings.length,
      deliverOnRun: output.deliverOnRun,
    };
  if (output.kind === 'app') return { kind: 'app', appId: output.appId };
  const components = reportCanvasComponents(output.layout);
  const tables = components.filter((item) => item.type === 'table').length;
  // A Text block charts nothing.
  const texts = components.filter((item) => item.type === 'text').length;
  return {
    kind: 'report',
    charts: components.length - tables - texts,
    tables,
    sheetNames: outputSheetIds(definition, output).flatMap((id) => {
      const name = sheetName(id);
      return name ? [name] : [];
    }),
    title: output.title?.trim() || undefined,
    description: output.description?.trim() || undefined,
  };
}
